import { useEffect, useState, useSyncExternalStore } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { session } from '../auth/session';
import { IconMenu, IconRefreshCw, IconX } from '../icons';
import { useTranslate } from '../i18n/react';
import { NAVIGATION } from '../layout/navigation';
import { Button, IconButton } from '../ui/Button';
import { updateReady, watchForUpdate } from '../main';
import { Sidebar } from '../ui/Sidebar';

/**
 * Dark rail on the left, content on the right, and nothing across the top.
 *
 * <p>Below 1024px the rail becomes a drawer: the same markup, slid over the content, with a
 * scrim behind it. The design package leaves this to us, and it matters more here than on
 * most products — the field screens are used on a phone.</p>
 */
export function Shell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useTranslate();
  const current = session();
  const [navOpen, setNavOpen] = useState(false);

  // A new version has downloaded and is waiting. It is not applied on its own: swapping the
  // code under a half-finished audit loses what the auditor typed, so they are told and
  // they choose the moment.
  const accept = useSyncExternalStore(watchForUpdate, updateReady, () => null);

  useEffect(() => {
    // Also the first real round trip of the session: a stale token fails here, and the
    // client refreshes or sends the user back to sign in before they have typed anything
    // they would then lose.
    api.tenant().catch(() => navigate('/giris', { replace: true }));
  }, [navigate]);

  // Following a link should not leave the drawer sitting over what it just opened.
  useEffect(() => setNavOpen(false), [pathname]);

  useEffect(() => {
    if (!navOpen) return;

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  async function signOut() {
    await api.signOut();
    navigate('/giris', { replace: true });
  }

  return (
    <div className="nv-app" data-nav-open={navOpen || undefined}>
      <a className="nv-skip-link" href="#main">{t('nav.skipToContent')}</a>

      <Sidebar groups={NAVIGATION} session={current} onSignOut={() => void signOut()} />

      {/* Only ever visible under the drawer breakpoint; hidden from assistive tech above it. */}
      <button
        type="button"
        className="nv-scrim"
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => setNavOpen(false)}
      />

      <main id="main" className="nv-main" tabIndex={-1}>
        {accept ? (
          <div className="nv-update" role="status">
            <span>{t('app.updateReady')}</span>
            <Button size="sm" icon={IconRefreshCw} onClick={accept}>
              {t('app.updateNow')}
            </Button>
          </div>
        ) : null}

        <div className="nv-main__mobile-bar">
          <IconButton
            label={navOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            icon={navOpen ? IconX : IconMenu}
            iconSize={20}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          />
        </div>

        <Outlet />
      </main>
    </div>
  );
}
